/**
 * useIssueTriageWatch — watches a single freshly-submitted issue for AI triage
 * completion, so the post-submit overlay can reveal clarifying questions the
 * moment they land.
 *
 * Triage runs asynchronously: an insert trigger fires the `triage-issues` edge
 * function, which (a few seconds later) flips `triage_status` to 'completed'
 * (or 'failed') and writes `suggested_questions`. We surface that via a realtime
 * subscription with a polling fallback (realtime can drop / be disabled), plus a
 * hard timeout so the UI never hangs if triage silently stalls.
 *
 * Intentionally standalone (no admin-only data plumbing) so it is safe to run for
 * ordinary reporters, who can SELECT their own issue row under RLS.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

export interface IssueTriageWatchState {
    /** True once triage reached a terminal state OR the timeout elapsed. */
    ready: boolean;
    /** True if triage genuinely completed/failed (vs. timing out). */
    settled: boolean;
    /** True if we gave up waiting before triage settled. */
    timedOut: boolean;
    triageStatus: string | null;
    issueStatus: string | null;
    questions: string[];
    confidence: number | null;
    /** True when AI triage flagged this issue as a duplicate of an existing one. */
    isDuplicate: boolean;
}

interface WatchedRow {
    triage_status: string | null;
    suggested_questions: string[] | null;
    triage_confidence: number | null;
    status: string | null;
    github_issue_number: number | null;
}

const TERMINAL = new Set(['completed', 'failed']);
const POLL_MS = 2500;
const DEFAULT_TIMEOUT_MS = 45000;

export function useIssueTriageWatch(
    issueId: string | null,
    opts: { timeoutMs?: number } = {},
): IssueTriageWatchState {
    const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const [row, setRow] = useState<WatchedRow | null>(null);
    const [timedOut, setTimedOut] = useState(false);

    useEffect(() => {
        if (!issueId) {
            setRow(null);
            setTimedOut(false);
            return;
        }
        const id = issueId; // non-null within this effect run

        let cancelled = false;
        setRow(null);
        setTimedOut(false);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let channel: any = null;

        const stopWatching = () => {
            clearInterval(interval);
            clearTimeout(timeout);
            if (channel) {
                supabase.removeChannel(channel);
                channel = null;
            }
        };

        const interval = setInterval(() => { void read(); }, POLL_MS);
        const timeout = setTimeout(() => {
            if (cancelled) return;
            setTimedOut(true);
            // Once we give up waiting, stop watching so a late-arriving triage
            // result can't pull the UI back out of its confirmation state.
            stopWatching();
        }, timeoutMs);

        const settleIfTerminal = (next: WatchedRow | null) => {
            if (next?.triage_status && TERMINAL.has(next.triage_status)) {
                stopWatching();
            }
        };

        async function read() {
            const { data, error } = await supabase
                .from('site_issues')
                .select('triage_status, suggested_questions, triage_confidence, status, github_issue_number')
                .eq('id', id)
                .single();
            if (cancelled || error || !data) return;
            const next = data as WatchedRow;
            setRow(next);
            settleIfTerminal(next);
        }

        // Prime immediately, then rely on realtime + polling.
        void read();

        if (typeof supabase.channel === 'function') {
            channel = supabase
                .channel(`issue_triage_watch_${id}`)
                .on(
                    'postgres_changes',
                    { event: 'UPDATE', schema: 'public', table: 'site_issues', filter: `id=eq.${id}` },
                    (payload) => {
                        if (cancelled) return;
                        const next = payload.new as WatchedRow;
                        setRow(next);
                        settleIfTerminal(next);
                    },
                )
                .subscribe();
        }

        return () => {
            cancelled = true;
            clearInterval(interval);
            clearTimeout(timeout);
            if (channel) supabase.removeChannel(channel);
        };
    }, [issueId, timeoutMs]);

    const triageStatus = row?.triage_status ?? null;
    const settled = !!triageStatus && TERMINAL.has(triageStatus);
    const questions = Array.isArray(row?.suggested_questions) ? row!.suggested_questions! : [];

    return {
        ready: settled || timedOut,
        settled,
        timedOut,
        triageStatus,
        issueStatus: row?.status ?? null,
        questions,
        confidence: row?.triage_confidence ?? null,
        isDuplicate: settled && row?.github_issue_number != null && row?.status === 'awaiting_admin_action',
    };
}
